import React, { ReactNode, useMemo } from 'react';
import { useGalleryConfig } from '../../context/GalleryConfigContext';
import { useGalleryTheme } from '../../context/GalleryThemeContext';
import GalleryHeader from './GalleryHeader';
import GalleryFooter from './GalleryFooter';
import GalleryNavigation from './GalleryNavigation';
import {
  getValidatedGoogleAnalyticsId,
  getValidatedFacebookPixelId,
} from '../../../../utils/analytics-validators';

interface GalleryLayoutProps {
  children: ReactNode;
}

const GalleryLayout: React.FC<GalleryLayoutProps> = ({ children }) => {
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();

  // Validate analytics IDs to prevent XSS attacks via dangerouslySetInnerHTML
  // See: https://github.com/owner/repo/issues/53
  const analyticsConfig = config?.settings?.analytics;
  const validatedAnalytics = useMemo(() => {
    if (!analyticsConfig) {
      return { googleAnalyticsId: null, facebookPixelId: null };
    }

    return {
      googleAnalyticsId: getValidatedGoogleAnalyticsId(
        analyticsConfig.googleAnalyticsId
      ),
      facebookPixelId: getValidatedFacebookPixelId(
        analyticsConfig.facebookPixelId
      ),
    };
  }, [analyticsConfig]);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Gallery...</h1>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: theme.colors.background,
        color: theme.colors.text.primary,
        fontFamily: theme.typography.fontFamily.primary,
      }}
    >
      {/* Gallery Header */}
      <GalleryHeader />

      {/* Navigation */}
      <GalleryNavigation />

      {/* Main Content */}
      <main className="flex-1">
        <div className="container mx-auto px-4 py-6">{children}</div>
      </main>

      {/* Gallery Footer */}
      <GalleryFooter />

      {/* Schema.org structured data */}
      {config.seo.structuredData.enabled && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': config.seo.structuredData.organizationType,
              name: config.businessName,
              description: config.seo.description,
              url: `https://${config.domain}`,
              ...(config.seo.structuredData.contactPoint && {
                contactPoint: {
                  '@type': 'ContactPoint',
                  telephone: config.seo.structuredData.contactPoint.telephone,
                  email: config.seo.structuredData.contactPoint.email,
                  url: config.seo.structuredData.contactPoint.website,
                  contactType: 'customer service',
                },
              }),
              ...(config.branding.logo && {
                logo: config.branding.logo,
              }),
            }),
          }}
        />
      )}

      {/* Analytics scripts - IDs are validated to prevent XSS */}
      {config.settings.analytics.enableTracking && (
        <>
          {validatedAnalytics.googleAnalyticsId && (
            <>
              <script
                async
                src={`https://www.googletagmanager.com/gtag/js?id=${validatedAnalytics.googleAnalyticsId}`}
              />
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${validatedAnalytics.googleAnalyticsId}');
                  `,
                }}
              />
            </>
          )}

          {validatedAnalytics.facebookPixelId && (
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${validatedAnalytics.facebookPixelId}');
                  fbq('track', 'PageView');
                `,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default GalleryLayout;
